import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

export interface ScraperSetting {
  id: string;
  scraper_name: string;
  enabled: boolean;
  enabled_ref: boolean;
  display_order: number;
  created_at: string;
  updated_at: string;
}

export function useScraperSettings() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: scrapers, isLoading } = useQuery({
    queryKey: ["scraper-settings"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("scraper_settings")
        .select("*")
        .order("display_order", { ascending: true });

      if (error) throw error;
      return data as ScraperSetting[];
    },
  });

  const toggleScraperMutation = useMutation({
    mutationFn: async ({ id, enabled }: { id: string; enabled: boolean }) => {
      const { error } = await supabase
        .from("scraper_settings")
        .update({ enabled })
        .eq("id", id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["scraper-settings"] });
      toast({
        title: "Scraper OEM mis à jour",
        description: "Le statut du scraper pour la recherche OEM a été modifié avec succès.",
      });
    },
    onError: (error) => {
      toast({
        variant: "destructive",
        title: "Erreur",
        description: `Impossible de mettre à jour le scraper: ${error.message}`,
      });
    },
  });

  const toggleScraperRefMutation = useMutation({
    mutationFn: async ({ id, enabled_ref }: { id: string; enabled_ref: boolean }) => {
      const { error } = await supabase
        .from("scraper_settings")
        .update({ enabled_ref })
        .eq("id", id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["scraper-settings"] });
      toast({
        title: "Scraper REF mis à jour",
        description: "Le statut du scraper pour la recherche par référence a été modifié avec succès.",
      });
    },
    onError: (error) => {
      toast({
        variant: "destructive",
        title: "Erreur",
        description: `Impossible de mettre à jour le scraper: ${error.message}`,
      });
    },
  });

  return {
    scrapers: scrapers || [],
    isLoading,
    toggleScraper: toggleScraperMutation.mutate,
    toggleScraperRef: toggleScraperRefMutation.mutate,
  };
}
